import i18n from 'i18next';
import { useObservableState } from 'observable-hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Observable } from 'rxjs';

import Table from '../../../../components/common/table/table';
import Form, { FormComponent } from '../../../../components/form';
import Amount from '../../../../components/form/elements/amount';
import Button from '../../../../components/form/elements/button';
import Date from '../../../../components/form/elements/date';
import Link from '../../../../components/form/elements/link';
import Percentage from '../../../../components/form/elements/percentage';
import Readonly from '../../../../components/form/elements/readonly';
import Show from '../../../../components/form/elements/show';
import Text from '../../../../components/form/elements/text';
import OptionModal from '../../../../components/modals/option-modal';
import { CropWorkType } from '../../../../enums/crop-work-types';
import Cargo from '../../../../interfaces/cargo';
import Paginate from '../../../../interfaces/paginate';
import {
  CROP_YIELD_ACCOUNTING_CARGO_EDIT,
  CROP_YIELD_ACCOUNTING_CARGO_INDEX,
  WAREHOUSE_MOVEMENT_INDEX,
} from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { can, route, routeQuery } from '../../../../services/utils';
import { RootState } from '../../../../store/store';
import css from './view.module.css';
import TabsKeys from '../../../../enums/tab-keys';

const DEAL_PRODUCTS_BLOCKING_DELETION = 'deal_products_blocking_deletion';

export default function YieldAccountingCargoView() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.yield_accounting.cargo.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const history = useHistory();
  const [footer, setFooter] = useState([]);
  const { farmId, season, yieldId, id } = useParams<{
    farmId: string;
    season: string;
    yieldId: string;
    id: string;
  }>();
  const { cargoService } = useService();

  const [body, setBody] = useState<FormComponent[]>(null);
  const [modal, setModal] = useState(null);

  const [cargo] = useObservableState<Cargo>(
    () => cargoService.show(farmId, yieldId, id),
    null
  );

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const requestWarehouses = useCallback(() => {
    const warehouses = cargo?.cargo_warehouses.map((w) => ({
      id: w.id,
      name: w.warehouse.name,
      quantity: w.quantity,
      unit: w.unit,
    }));
    return new Observable<Paginate<any>>((subscriber) =>
      subscriber.next({
        data: warehouses,
      } as Paginate<any>)
    );
  }, [cargo]);

  const deleteModalYesButtonText = useCallback(() => {
    if (cargo?.[DEAL_PRODUCTS_BLOCKING_DELETION].length) {
      return tp('move_to_deals');
    }

    return t('yes');
  }, [cargo, t, tp]);

  const deleteModalContentText = useCallback(() => {
    if (cargo?.[DEAL_PRODUCTS_BLOCKING_DELETION].length) {
      return (
        <div className={css['modal-delete_description']}>
          {tp('description_move_to_deals')}
        </div>
      );
    }

    return tp('delete_warning');
  }, [cargo, tp]);

  const deleteModalTitleText = useCallback(() => {
    if (cargo?.[DEAL_PRODUCTS_BLOCKING_DELETION].length) {
      return tp('relationship_deals_title');
    }

    return undefined;
  }, [cargo, tp]);

  const deleteModal = useCallback(() => {
    return (
      <OptionModal
        title={deleteModalTitleText()}
        content={deleteModalContentText()}
        buttons={[
          {
            title: deleteModalYesButtonText(),
            name: 'yes',
            value: true,
          },
          {
            title: cargo?.[DEAL_PRODUCTS_BLOCKING_DELETION].length
              ? t('cancel')
              : t('no'),
            name: 'no',
            value: false,
          },
        ]}
        onChoice={(choice) => {
          if (choice) {
            if (cargo?.[DEAL_PRODUCTS_BLOCKING_DELETION].length) {
              sessionStorage.removeItem('current_index_page');
              history.push(
                routeQuery(
                  route(WAREHOUSE_MOVEMENT_INDEX, {
                    farmId: farmId,
                    tab: TabsKeys.deals,
                  }),
                  {
                    ids: cargo?.[DEAL_PRODUCTS_BLOCKING_DELETION],
                  }
                )
              );
            } else {
              cargoService.destroy(farmId, yieldId, id).subscribe(() => {
                history.push(
                  route(CROP_YIELD_ACCOUNTING_CARGO_INDEX, {
                    farmId: farmId,
                    season: season,
                    yield_id: yieldId,
                  })
                );
              });
            }
          }

          setModal(null);
        }}
        onClose={() => setModal(null)}
      />
    );
  }, [
    t,
    cargo,
    cargoService,
    history,
    farmId,
    season,
    yieldId,
    id,
    deleteModalTitleText,
    deleteModalContentText,
    deleteModalYesButtonText,
  ]);

  const destroy = useCallback(() => {
    setModal(deleteModal);
  }, [deleteModal]);

  useEffect(() => {
    setBody([
      Readonly([
        Text('work_type_name', tp('work_type_name')),
        Date('work.date', tp('work_date')),
        Text(
          'workField.crop_value',
          t('pages.crop.fields_and_works.fields.crop')
        ),
        Text('workField.breed', t('pages.crop.fields_and_works.fields.breed')),
        Text('product_type.value', tp('product_type')),
        Amount({ amount: 'gross', unit: 'unit' }, { label: tp('gross') }),
        Amount({ amount: 'tare', unit: 'unit' }, { label: tp('tare') }),
        Show(
          [
            Amount({ amount: 'net', unit: 'unit' }, { label: tp('net') }),
            Percentage('impurities', tp('impurities')),
            Amount(
              { amount: 'impurities_total', unit: 'unit' },
              { label: tp('impurities_total') }
            ),
            Percentage('base_humidity', tp('base_humidity')),
            Percentage('humidity', tp('humidity')),
            Amount(
              { amount: 'humidity_above_norm', unit: 'unit' },
              { label: tp('humidity_above_norm') }
            ),
          ],
          'work.type_id',
          (v) => v === CropWorkType.THRESHING
        ),
        Amount({ amount: 'quantity', unit: 'unit' }, { label: tp('quantity') }),
      ]),
      () => (
        <div className={css['warehouses']}>
          <h3>{tp('warehouses')}</h3>
          <Table<any>
            request={requestWarehouses}
            paginate={false}
            keyAccessor="id"
            columns={[
              {
                accessor: 'name',
                header: t('pages.warehouse.warehouses.name'),
              },
              {
                accessor: ['quantity', 'unit'],
                header: tp('warehouse_quantity'),
              },
            ]}
          />
        </div>
      ),
    ]);

    setFooter(() => {
      let elements = [];

      if (can(farmUser, { title: 'update cargo' })) {
        elements.push(
          Link(
            route(CROP_YIELD_ACCOUNTING_CARGO_EDIT, {
              farmId: farmId,
              season: season,
              yieldId: yieldId,
              id: id,
            }),
            'edit',
            true
          )
        );
      }

      if (can(farmUser, { title: 'delete cargo' })) {
        elements.push(Button('delete', destroy));
      }

      return elements;
    });
  }, [
    t,
    tp,
    cargo,
    requestWarehouses,
    destroy,
    farmId,
    farmUser,
    id,
    season,
    yieldId,
  ]);

  if (!cargo) {
    return null;
  }

  cargo.work_type_name = i18n.t(
    'classifiers.properties.crop_work_type.'.concat(cargo.work.type_id)
  );

  return (
    <>
      <Form
        mode="view"
        title={`${cargo.field.label}`}
        backTo={route(CROP_YIELD_ACCOUNTING_CARGO_INDEX, {
          farmId: farmId,
          season: season,
          yield_id: yieldId,
        })}
        body={body}
        footer={footer}
        values={cargo}
      />
      {modal}
    </>
  );
}
